'use client';

import { ProductDetailGiftPayload } from '@/api/types/product';
import Image from '@/components/Image';
import Popup from '@/components/Popup';
import { useTranslations } from '@/i18n/use-translations';
import { useStore } from '@/providers/app-store-provider';
import { formatPriceNumberWithSymbol } from '@/utils/price';
import { Dialog, IconButton } from '@mui/material';
import { CloseOutlined, RightArrowOutlined } from '@zeelool/icon';
import React, { useMemo, useState } from 'react';
import { Swiper as SwiperType } from 'swiper';
import { Swiper, SwiperSlide } from 'swiper/react';

interface GiftTeaserProps {
  giftData?: ProductDetailGiftPayload | null;
  isWeb?: boolean;
  className?: string;
  SDProductDetailsPageBtnClick?: (btnName: string) => void;
}

interface GiftInfo {
  title: string;
  image: string;
  images: string[];
  color?: string;
  sellPrice?: string;
  oldPrice?: string;
  stockText?: string;
  eventPeriod?: string;
  rules: string[];
}

type GiftImageMap = Partial<Record<string, string[]>>;

const formatEventPeriod = (startTime?: string | number, endTime?: string | number) => {
  if (!startTime || !endTime) return undefined;

  return `${startTime} - ${endTime}`;
};

const getGiftImagesFromMap = (images?: GiftImageMap) => {
  if (!images) return [];

  const imageOrder = [
    'product_gray',
    'product',
    'product_detail',
    'material',
    'celebrity',
    'video',
  ];

  const orderedImages = imageOrder.flatMap((key) => images[key] || []);
  const restImages = Object.entries(images)
    .filter(([key]) => !imageOrder.includes(key))
    .flatMap(([, value]) => value || []);

  return [...orderedImages, ...restImages].filter(Boolean);
};

const getGiftInfo = (giftData?: ProductDetailGiftPayload | null): GiftInfo | null => {
  if (giftData?.show !== true) return null;

  const images = getGiftImagesFromMap(giftData.images);
  const gift = giftData?.gift;
  const activity = giftData?.activity;
  const image = images[0];
  const title = gift?.short_name || gift?.name;

  return {
    title,
    image,
    images,
    color: gift?.color,
    sellPrice: gift?.sell_price,
    oldPrice: gift?.price,
    stockText: gift?.stock_text,
    eventPeriod: formatEventPeriod(activity?.start_time, activity?.end_time),
    rules: activity?.description
      ? activity.description
          .split(/\n+/)
          .map((item) => item.trim())
          .filter(Boolean)
      : [],
  };
};

const GiftImage = ({
  gift,
  isWeb,
  className = '',
}: {
  gift: GiftInfo;
  isWeb?: boolean;
  className?: string;
}) => (
  <div
    className={`relative flex shrink-0 items-center justify-center overflow-hidden bg-auxiliary-light-gray ${className}`}
  >
    <Image
      src={gift.image}
      alt={gift.title}
      width={isWeb ? 560 : 375}
      height={isWeb ? 560 : 375}
      className="h-auto w-full mix-blend-multiply"
    />
  </div>
);

const GiftCarousel = ({
  gift,
  isWeb,
  className = '',
}: {
  gift: GiftInfo;
  isWeb?: boolean;
  className?: string;
}) => {
  const [activeIndex, setActiveIndex] = useState(0);
  const images = gift.images.length > 0 ? gift.images : [gift.image];

  const handleSlideChange = (swiper: SwiperType) => {
    setActiveIndex(swiper.activeIndex);
  };

  return (
    <div className={`relative overflow-hidden ${className}`}>
      <Swiper className="h-full w-full" onSlideChange={handleSlideChange}>
        {images.map((image, index) => (
          <SwiperSlide
            key={`${image}-${index}`}
            className="!flex items-center justify-center bg-auxiliary-light-gray"
          >
            <Image
              src={image}
              alt={`${gift.title}${index + 1}`}
              width={isWeb ? 400 : 375}
              height={isWeb ? 400 : 375}
              className="h-auto w-full object-contain mix-blend-multiply "
            />
          </SwiperSlide>
        ))}
      </Swiper>
      <span
        className={`absolute z-2 bg-[#999999] text-white flex items-center justify-center ${
          isWeb
            ? 'bottom-[16px] left-[16px] rounded-[14px] w-[50px] h-[20px] text-[12px] leading-[14px]'
            : 'bottom-[14px] left-[14px] rounded-[16px] bg-[rgba(0,0,0,0.4)] w-[66px] h-[24px] text-[11px] leading-[13px]'
        }`}
      >
        {activeIndex + 1}/{images.length}
      </span>
    </div>
  );
};

const GiftDetailContent = ({
  gift,
  isWeb,
  onClose,
}: {
  gift: GiftInfo;
  isWeb?: boolean;
  onClose: () => void;
}) => {
  const t = useTranslations('goodsDetail');
  const dialogT = useTranslations('dialog');
  const currency = useStore((s) => s.currency);
  const sellPrice = gift.sellPrice
    ? formatPriceNumberWithSymbol(gift.sellPrice, currency)
    : undefined;
  const oldPrice =
    gift.oldPrice && gift.oldPrice !== gift.sellPrice
      ? formatPriceNumberWithSymbol(gift.oldPrice, currency)
      : undefined;

  if (isWeb) {
    return (
      <div className="relative flex h-[464px] gap-[32px] overflow-hidden rounded-8 bg-white">
        <IconButton
          aria-label={dialogT('closeAriaLabel')}
          onClick={onClose}
          className="!absolute right-[14px] top-[14px] !p-[0px]"
        >
          <CloseOutlined className="text-28 text-primary-text" />
        </IconButton>
        <div className="relative shrink-0 p-[32px]">
          <GiftCarousel gift={gift} isWeb className="h-[400px] w-[400px]" />
        </div>
        <div className="flex min-h-0 min-w-0 flex-1 flex-col pt-[32px] text-primary-text">
          <div className="pr-[32px]">
            <h3 className="font-bb text-[32px] leading-[39px]">{gift.title}</h3>
            {gift.color && (
              <p className="mt-[8px] text-[14px] leading-[17px] text-[#808080]">
                {t('color')} {gift.color}
              </p>
            )}
            {(sellPrice || oldPrice) && (
              <div className="mt-[24px] flex items-center gap-[8px]">
                {sellPrice && (
                  <span className="font-bb text-[24px] leading-[28px]">{sellPrice}</span>
                )}
                {oldPrice && (
                  <span className="text-[14px] leading-[17px] line-through">{oldPrice}</span>
                )}
              </div>
            )}
            <div className="mt-[24px]">
              {gift.stockText && (
                <p className="text-[12px] leading-[14px] mb-[8px]">{gift.stockText}</p>
              )}
              <p className="text-[12px] leading-[14px]">{t('freeGift.comeWithProduct')}</p>
            </div>
          </div>
          {(gift.eventPeriod || gift.rules.length > 0) && (
            <div className="mt-[16px] flex min-h-0 flex-1 flex-col">
              <div className="mr-[32px] border-t border-[#dce0e8]" />
              <div className="mt-[16px] min-h-0 flex-1 overflow-auto pb-[32px] pr-[32px]">
                {gift.eventPeriod && (
                  <p className="font-bb text-[16px] leading-[19px]">
                    {t('freeGift.eventPeriod', { period: gift.eventPeriod })}
                  </p>
                )}
                {gift.rules.length > 0 && (
                  <ol className="mt-[16px] space-y-[16px] text-[14px] leading-[17px]">
                    {gift.rules.map((rule) => (
                      <li key={rule}>{rule}</li>
                    ))}
                  </ol>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-full flex-col bg-white">
      <div className="relative h-[52px] shrink-0 bg-white text-center">
        <h3 className="font-bb text-18 leading-[52px] text-primary-text">
          {t('freeGift.teaserTitle')}
        </h3>
        <IconButton
          aria-label={dialogT('closeAriaLabel')}
          onClick={onClose}
          className="!absolute right-[10px] top-[8px] !p-4"
        >
          <CloseOutlined className="text-26 text-primary-text" />
        </IconButton>
      </div>
      <div className="min-h-0 flex-1 overflow-auto">
        <GiftCarousel gift={gift} className="h-[375px] w-full" />
        <div className="px-[14px] pb-34 pt-[15px] text-primary-text">
          <div className="flex items-center gap-[8px]">
            <h4 className="font-bb text-[14px] leading-[17px]">{gift.title}</h4>
            {gift.color && <span className="text-[14px] leading-[17px]">{gift.color}</span>}
          </div>
          {(sellPrice || oldPrice) && (
            <div className="mt-[16px] flex items-center gap-[8px]">
              {sellPrice && <span className="font-bb text-[20px] leading-[24px]">{sellPrice}</span>}
              {oldPrice && (
                <span className="text-[14px] leading-[17px] line-through text-[#a9a9a9]">
                  {oldPrice}
                </span>
              )}
            </div>
          )}
          <div className="mt-[16px]">
            {gift.stockText && (
              <p className="text-[12px] leading-[14px] mb-[6px]">{gift.stockText}</p>
            )}
            <p className="text-[12px] leading-[14px]">{t('freeGift.comeWithProduct')}</p>
          </div>

          {(gift.eventPeriod || gift.rules.length > 0) && (
            <div className="mt-[16px] border-t border-[#dce0e8] border-t-[0.5px] pt-[16px]">
              {gift.eventPeriod && (
                <p className="font-bb text-[14px] leading-[17px]">
                  {t('freeGift.eventPeriod', { period: gift.eventPeriod })}
                </p>
              )}
              {gift.rules.length > 0 && (
                <ol className="mt-[12px] space-y-[8px] text-[12px] leading-[14px]">
                  {gift.rules.map((rule) => (
                    <li key={rule}>{rule}</li>
                  ))}
                </ol>
              )}
            </div>
          )}
        </div>
      </div>
      <div
        className="bg-white px-[16px] pb-[calc(14px+env(safe-area-inset-bottom))] pt-[14px]"
        style={{ boxShadow: '0px -1px 5px 0px rgba(204,204,204,0.5)' }}
      >
        <button
          type="button"
          onClick={onClose}
          className="h-[43px] w-full rounded-[24px] bg-primary-purple font-bb text-[14px] leading-[17px] text-white"
        >
          {t('freeGift.close')}
        </button>
      </div>
    </div>
  );
};

export default function GiftTeaser({
  giftData,
  isWeb = false,
  className = '',
  SDProductDetailsPageBtnClick,
}: GiftTeaserProps) {
  const [open, setOpen] = useState(false);
  const t = useTranslations('goodsDetail');
  const gift = useMemo(() => getGiftInfo(giftData), [giftData]);

  if (!gift) {
    return null;
  }

  const handleOpen = () => {
    SDProductDetailsPageBtnClick?.('Free Gift');
    setOpen(true);
  };

  const handleClose = () => setOpen(false);

  return (
    <>
      <div
        onClick={handleOpen}
        className={`cursor-pointer flex items-center border text-left ${isWeb ? 'bg-white border-[#dce0e8] w-full rounded-4 pl-[4px] py-[4px] pr-[12px]' : 'bg-[rgba(255,255,255,0.5)] border-[rgba(255,255,255,0.5)] rounded-[4px] pl-[3px] py-[3px] pr-[8px]'} ${className}`}
      >
        <GiftImage
          gift={gift}
          isWeb={isWeb}
          className={isWeb ? 'h-[48px] w-[48px] rounded-[4px]' : 'h-[40px] w-[40px] rounded-[4px]'}
        />
        <span className={`${isWeb ? 'ml-[8px]' : 'ml-[6px]'} min-w-0 flex-1`}>
          <span
            className={`block font-bs text-[#282828] ${isWeb ? 'text-[14px] leading-[17px]' : 'text-[12px] leading-[14px]'}`}
          >
            {t('freeGift.teaserTitle')}
          </span>
          <span
            className={`block text-[#808080] ${isWeb ? 'text-[12px] leading-[14px] mt-[4px]' : 'text-[10px] leading-[12px] mt-[2px] truncate'}`}
          >
            {gift.title}
          </span>
        </span>
        {isWeb && <RightArrowOutlined className="ml-8 text-[16px] text-[#282828]" />}
      </div>

      {isWeb ? (
        <Dialog
          open={open}
          onClose={handleClose}
          maxWidth={false}
          PaperProps={{
            sx: {
              width: '864px',
              maxWidth: '864px',
              maxHeight: '80vh',
              borderRadius: '12px',
              boxShadow: 'none',
              overflow: 'hidden',
            },
          }}
        >
          <GiftDetailContent gift={gift} isWeb onClose={handleClose} />
        </Dialog>
      ) : (
        <Popup
          open={open}
          onClose={handleClose}
          position="bottom"
          height="80%"
          round
          borderRadius={16}
          hideCloseIcon
          title={null}
          bodyClassName="!p-0"
          childrenMaxHeight="none"
        >
          <GiftDetailContent gift={gift} onClose={handleClose} />
        </Popup>
      )}
    </>
  );
}
